import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon, AutoAwesome as GenerateIcon } from '@mui/icons-material';
import { copilotApi } from '../../services/copilotApi';
import { UserWritingTemplate, UserWritingTemplateInput } from '../../types/documents';

interface TemplateEditorDialogProps {
  open: boolean;
  template: UserWritingTemplate | null; // null = create new
  onSave: (template: UserWritingTemplate) => void;
  onClose: () => void;
}

const TemplateEditorDialog: React.FC<TemplateEditorDialogProps> = ({
  open,
  template,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [useCase, setUseCase] = useState('');
  const [instructions, setInstructions] = useState('');
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes or template changes
  useEffect(() => {
    if (open) {
      if (template) {
        setName(template.name);
        setUseCase(template.useCase);
        setInstructions(template.instructions);
        setContent(template.content);
        setIsActive(template.isActive);
      } else {
        setName('');
        setUseCase('');
        setInstructions('');
        setContent('');
        setIsActive(true);
      }
      setError(null);
    }
  }, [open, template]);

  const handleGenerateContent = async () => {
    if (!useCase.trim()) {
      setError('Please enter a use case before generating content.');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const generatedContent = await copilotApi.generateTemplateContent({
        useCase: useCase.trim(),
        instructions: instructions.trim(),
      });
      setContent(generatedContent);
    } catch (err) {
      console.error('Failed to generate content:', err);
      setError('Failed to generate content. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    // Validate
    if (!name.trim()) {
      setError('Template name is required.');
      return;
    }
    if (!useCase.trim()) {
      setError('Use case description is required.');
      return;
    }
    if (!content.trim()) {
      setError('Template content is required.');
      return;
    }

    const input: UserWritingTemplateInput = {
      name: name.trim(),
      useCase: useCase.trim(),
      instructions: instructions.trim(),
      content: content.trim(),
      isActive,
    };

    try {
      setSaving(true);
      setError(null);

      let savedTemplate: UserWritingTemplate;
      if (template) {
        // Update existing
        savedTemplate = await copilotApi.updateWritingTemplate(template.templateId, input);
      } else {
        // Create new
        savedTemplate = await copilotApi.createWritingTemplate(input);
      }

      onSave(savedTemplate);
    } catch (err) {
      console.error('Failed to save template:', err);
      setError('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!template;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">
          {isEditing ? 'Edit Template' : 'Create Template'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            placeholder="e.g., Discharge Papers"
            helperText="A short name for this template"
          />

          <TextField
            label="Use Case"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
            placeholder="e.g., Use this template when writing discharge papers or going-home instructions for pet owners"
            helperText="Describe when this template should be used. The AI uses this to select the right template."
          />

          <TextField
            label="Style Instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="e.g., Friendly but professional tone. Always include our clinic's standard disclaimer at the end."
            helperText="Optional: Describe the tone, style, or specific requirements for this template"
          />

          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">
                Template Content <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={generating ? <CircularProgress size={16} /> : <GenerateIcon />}
                onClick={handleGenerateContent}
                disabled={generating || !useCase.trim()}
              >
                {generating ? 'Generating...' : 'Generate Example'}
              </Button>
            </Box>
            <TextField
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              required
              multiline
              rows={12}
              placeholder="Enter your template content here, or click 'Generate Example' to create a starting point based on your use case..."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Write your template in markdown format. The AI will adapt this template to each specific case while preserving your style.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Template is active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || !useCase.trim() || !content.trim()}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
        >
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditorDialog;
